import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { calculateChurnRisk } from '../../../services/aiAnalytics';
import toast from 'react-hot-toast';

const ChurnRiskWarnings = ({ churnData = [] }) => {
  const navigate = useNavigate();
  const [aiChurnAnalysis, setAiChurnAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to calculate churn risk');
    }
  }, [error]);

  useEffect(() => {
    if (churnData?.length > 0) {
      loadChurnAnalysis();
    }
  }, [churnData]);

  const loadChurnAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await calculateChurnRisk(churnData);
      setAiChurnAnalysis(result);
    } catch (err) {
      console.error('Churn analysis error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (category) => {
    switch (category) {
      case 'critical':
        return 'text-danger-600';
      case 'high':
        return 'text-danger-500';
      case 'medium':
        return 'text-warning-600';
      default:
        return 'text-success-600';
    }
  };

  const getRiskBgColor = (category) => {
    switch (category) {
      case 'critical':
        return 'bg-danger-50 border-danger-200';
      case 'high':
        return 'bg-danger-50 border-danger-200';
      case 'medium':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-success-50 border-success-200';
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-warning-100 rounded-lg">
            <Icon name="AlertTriangle" className="w-5 h-5 text-warning-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Churn Risk Analysis</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Card>
    );
  }

  const criticalTenants = aiChurnAnalysis?.analyses?.filter(a => a?.riskCategory === 'critical' || a?.riskCategory === 'high') || [];
  const totalAtRisk = criticalTenants?.length || 0;

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-warning-100 rounded-lg">
            <Icon name="AlertTriangle" className="w-5 h-5 text-warning-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Churn Risk Analysis</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <button
          onClick={loadChurnAnalysis}
          disabled={loading}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          title="Refresh analysis"
        >
          <Icon name="RefreshCw" className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {aiChurnAnalysis?.analyses?.length > 0 ? (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-danger-600">{totalAtRisk}</div>
              <div className="text-xs text-muted-foreground">High Risk Tenants</div>
            </div>
            <div className="bg-muted border border-border rounded-lg p-4">
              <div className="text-2xl font-bold text-foreground">
                {aiChurnAnalysis?.analyses?.length || 0}
              </div>
              <div className="text-xs text-muted-foreground">Total Analyzed</div>
            </div>
          </div>

          {/* Top Risk Tenants */}
          {criticalTenants?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Users" className="w-4 h-4" />
                Critical Risk Tenants
              </h4>
              <div className="space-y-3">
                {criticalTenants?.slice(0, 3)?.map((tenant, index) => {
                  const originalTenant = churnData?.find(t => t?.id === tenant?.tenantId);
                  return (
                    <div key={index} className={`p-3 rounded-lg border ${getRiskBgColor(tenant?.riskCategory)}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-foreground">
                            {originalTenant?.companyName || originalTenant?.name || 'Unknown Tenant'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {tenant?.riskCategory?.toUpperCase()} - {tenant?.churnProbability?.toFixed(0)}% probability
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">
                        <strong>Risk Factors:</strong> {tenant?.riskFactors?.join(', ')}
                      </div>
                      <div className="text-xs text-foreground">
                        <strong>Strategy:</strong> {tenant?.retentionStrategy}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            onClick={() => navigate('/tenant-management')}
            variant="outline"
            className="w-full"
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All Tenants
          </Button>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="CheckCircle" className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No churn risk data available</p>
        </div>
      )}
    </Card>
  );
};

export default ChurnRiskWarnings;